import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Camera, CameraOff } from "lucide-react";
import { cameraLabels, measureQuality } from "../../lib/kycApi";
import { BigButton, GhostButton } from "./KycShell";

const FRAMES = {
  documento: "w-[86%] aspect-[1.586] rounded-xl",
  cara: "w-[62%] aspect-[3/4] rounded-[50%]",
  cara_documento: "w-[80%] aspect-[4/3] rounded-2xl",
};

export const useCamera = (facing) => {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let s;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: facing, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false })
      .then((st) => {
        s = st;
        setStream(st);
        if (videoRef.current) videoRef.current.srcObject = st;
      })
      .catch(() => setError("No pudimos abrir la cámara. Revisa que diste permiso a la cámara en tu navegador."));
    return () => s?.getTracks().forEach((t) => t.stop());
  }, [facing]);
  const label = () => stream?.getVideoTracks()[0]?.label || "";
  return { videoRef, stream, error, label };
};

export const CameraView = ({ videoRef, frame }) => (
  <div className="relative w-full aspect-[3/4] sm:aspect-[4/3] bg-black rounded-2xl overflow-hidden">
    <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" data-testid="camera-video" />
    {frame && (
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className={`${FRAMES[frame]} border-4 border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]`} />
      </div>
    )}
  </div>
);

export const CameraError = ({ msg }) => (
  <div className="flex gap-3 p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-900 text-base" data-testid="camera-error">
    <CameraOff className="shrink-0" /> {msg}
  </div>
);

export const PhotoCapture = ({ facing, frame, hint, color, onConfirm, busy }) => {
  const { videoRef, error, label } = useCamera(facing);
  const [shot, setShot] = useState(null);

  const capture = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    const calidad = measureQuality(c);
    c.toBlob((blob) => setShot({ blob, url: URL.createObjectURL(blob), calidad }), "image/jpeg", 0.9);
  };

  const confirm = async () => {
    onConfirm(shot.blob, { camara: label(), camaras: await cameraLabels(), calidad: shot.calidad, fuente: "camara_en_vivo" });
  };

  if (error) return <CameraError msg={error} />;
  if (shot) {
    const warn = shot.calidad.borrosa || shot.calidad.con_reflejo;
    return (
      <div className="space-y-4" data-testid="photo-preview">
        <img src={shot.url} alt="Foto tomada" className="w-full rounded-2xl" />
        {warn && (
          <div className="flex gap-3 p-4 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 text-base" data-testid="photo-quality-warning">
            <AlertTriangle className="shrink-0" />
            <span>
              {shot.calidad.borrosa && "La foto se ve borrosa. "}
              {shot.calidad.con_reflejo && "Hay un reflejo de luz. "}
              Te recomendamos tomarla de nuevo.
            </span>
          </div>
        )}
        <BigButton color={color} disabled={busy} onClick={warn ? () => setShot(null) : confirm} data-testid="photo-primary-button">
          {warn ? "Tomar de nuevo" : busy ? "Enviando…" : "Usar esta foto"}
        </BigButton>
        <GhostButton disabled={busy} onClick={warn ? confirm : () => setShot(null)} data-testid="photo-secondary-button">
          {warn ? "Usar igual" : "Tomar de nuevo"}
        </GhostButton>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="photo-capture">
      <CameraView videoRef={videoRef} frame={frame} />
      <p className="text-base text-slate-700 text-center">{hint}</p>
      <BigButton color={color} onClick={capture} data-testid="photo-take-button">
        <span className="inline-flex items-center gap-2"><Camera /> Tomar foto</span>
      </BigButton>
    </div>
  );
};
